import {
  isBaseline,
  isCore,
  isCorePaid,
  isCoreWinback,
  isEn,
  isEnUS,
  isEnIntl,
  isInApp,
  isInApp14DayiOS,
  isWinback,
  isEmailMain,
  isIOS,
  isEnUSWinback,
  isApp14DayBaseRoute,
  isEnBaseline,
  isDeLanguage,
  isCountry,
  isCoreDirect,
  isEnIntlBaseline,
} from ".";
import { routeConstants } from "../constants";
import store, { CoreReduxState } from "../redux/store";

import { isFromEmail, isEmailWinback, isEnUSEmail } from "./email";
import { isUS } from "./locale";
import { checkSegments } from "./util";
import {
  ELIGIBLE_HEALTH_RISKS,
  EligibleStateCode,
  OPTIMIZELY_TARGETED_FOR_TELEHEALTH,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import {
  calculateCurrentBMIImperial,
  getCourseDuration,
} from "@utils/calculateGoals";
import { getIsTelehealthPlan } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/session-storage-getsets/getSetIsTelehealthPlan";
import { getTrackingParams } from "../services/VisitTracker";
import { canRetrieveWeight } from "../refactoredSurvey";

const devModeFlagSet =
  new URLSearchParams(window.location.search).get("idm") === "true";
export function isDevMode() {
  return devModeFlagSet;
}

export const isCollapsedPaymentEligible = (state?: CoreReduxState) => {
  return isEn(state) && !isInApp(state);
};

export const isHMEligible = (state?: CoreReduxState) =>
  isEnUS(state) && isCore(state) && isIOS();

/**
 * Verifies that the user is eligible for Compass styles.
 * Currently only baselined for all English buyflows
 */
export const isCompassEligible = isEn;

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  required: isEnUS,
  oneOf: [isInApp14DayiOS],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [
    routeConstants.app14DayFtWeb,
    routeConstants.app14DayWeb,
    routeConstants.app14DayWebReturning,
  ],
});

export const isFastForwardEligible = checkSegments({
  required: (state?: CoreReduxState) => isEnUS(state) && !getIsTelehealthPlan(),
  oneOf: [isCore, routeConstants.winback],
});

const isNoomPremiumEligibleUS = checkSegments({
  required: isEnUS,
  oneOf: [
    isBaseline,
    isCoreWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
  ],
});

const isNoomPremiumEligibleIntl = checkSegments({
  required: isEnIntl,
  oneOf: [
    isBaseline,
    isCoreWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
    routeConstants.freeTrial,
  ],
});

export const isNoomPremiumEligible = () => {
  return isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();
};

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [
    isBaseline,
    routeConstants.winback,
    routeConstants.app14DayWeb,
    routeConstants.app14Day,
  ],
});

export const isConsultationEligible = checkSegments({
  required: isUS,
  oneOf: [
    isBaseline,
    routeConstants.affiliateTrialNPB,
    routeConstants.referrals,
  ],
});

export const isImportantDateEligible = checkSegments({
  required: isEnUS,
  oneOf: [isBaseline, isCoreWinback, isFromEmail],
});

export const isFivePercentEligible = checkSegments({
  required: isEnUS,
  oneOf: [isBaseline],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [isBaseline, isWinback, isEnUSEmail, isInApp],
});

export const isBuildPlanEligible = (state = store.getState()) => {
  return (
    isInAppWebPurchaseEligible(state) ||
    isBaseline(state) ||
    isWinback(state) ||
    (isEnUS(state) && isEmailWinback(state))
  );
};

export const isFinalGraphEligible = checkSegments({
  oneOf: [isEnUSWinback, isEnBaseline, isEnUSEmail, isApp14DayBaseRoute],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = ({ userData } = store.getState()) => {
  const { weight, idealWeight } = userData[userData?.surveyNameSpace] || {};
  const courseDuration = getCourseDuration();

  // EnUSBaseline route
  if (weight && idealWeight && isFivePercentEligible() && courseDuration <= 8) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

export const shouldCondenseGraphSteps = () => {
  const courseDuration = getCourseDuration();
  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isEnIntlBaseline()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const isTrialEligible = (
  { recommendedPlan, serverContext } = store.getState()
) => {
  return recommendedPlan.has_trial && !serverContext.has_no_trial;
};

export function showChooseTrialPage(state = store.getState()) {
  if (!state.serverContext.trial_fees) {
    return false;
  }
  if (isEn(state)) {
    return state.serverContext.choose_trial_fee;
  }
  return isCoreDirect(state) || isCorePaid(state);
}

export const isPromoCodeBoxDisabled = checkSegments({
  required: (state?: CoreReduxState) => isEn(state) || isDeLanguage(state),
  oneOf: [isInApp, isCorePaid, isWinback, routeConstants.affiliateNoTrial],
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [
    routeConstants.app14DayWebReturning,
    routeConstants.app14DayWeb,
    routeConstants.app14DayFtWeb,
    routeConstants.app14Day,
    routeConstants.appNoTrial,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    routeConstants.app14Day,
    routeConstants.app14DayWeb,
  ],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    routeConstants.app14Day,
    routeConstants.app14DayWeb,
    routeConstants.hmPaid,
    routeConstants.hmDirect,
  ],
});

// used exclusively for users coming through the initial buyflow
// determines if the user will even see the insurance reimbursement survey
export const isEligibleForTelehealthReimbursement = (
  { surveyAnswers, geoLocation } = store.getState()
): boolean => {
  // this value is set (and can be turned on/off, throttled) by Optimizely
  // (2021/08/10) https://app.optimizely.com/v2/projects/2753280231/experiments/20510260333
  const optimizelyTargetedForTelehealth = sessionStorage.getItem(
    OPTIMIZELY_TARGETED_FOR_TELEHEALTH
  );

  const isEligibleState =
    geoLocation?.country_code === "US" &&
    geoLocation?.subdivision in EligibleStateCode;

  let currentBMI;
  const { heightFeet, heightInch, weight } = surveyAnswers;

  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weight !== undefined
  ) {
    currentBMI = parseFloat(
      calculateCurrentBMIImperial(heightFeet, heightInch, weight)
    );
  }

  const userHealthRisks: string[] = (
    surveyAnswers?.currentHealthRisk ?? []
  ).concat(surveyAnswers?.currentHealthRiskMale ?? []);
  const isEligibleHealthRisk = userHealthRisks.some((risk) =>
    ELIGIBLE_HEALTH_RISKS.includes(risk)
  );

  return (
    optimizelyTargetedForTelehealth &&
    isEligibleState &&
    isCore() &&
    (currentBMI >= 30 || (currentBMI >= 25 && isEligibleHealthRisk))
  );
};

export const isTelehealthPlan = () => getIsTelehealthPlan();

export const shouldRedirectToProgramSwitch = (
  { activeSubscriptionData } = store.getState()
): boolean => {
  return (
    activeSubscriptionData.hasActiveSubscription &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.endsWith("en_US") &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.includes(
      "HEALTHY_WEIGHT"
    ) &&
    !activeSubscriptionData.activeSubscriptionCurriculumSlug.includes(
      "HEALTHY_WEIGHT_LIFE_MODE"
    ) &&
    !activeSubscriptionData.isIAPsubscription
  );
};

export const isEligibleForExitIntentModalEmail = () => {
  return (
    isEnIntl() && isEmailMain() && getTrackingParams().utm_source === "drip"
  );
};

export const isTaxExclusiveCountry = (state?: CoreReduxState) =>
  isCountry(["US", "CA"], state);
